import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { IonInput, IonSpinner } from '@ionic/react';
import { ImpactStyle } from '@capacitor/haptics';
import api from '../../../lib/api';
import { useCapacitorPlatform } from '@shared/hooks/useCapacitorPlatform';
import ProPageModal from './ProPageModal';
import './ProForgotPasswordSheet.css';

export interface ProForgotPasswordSheetProps {
  /** Controls visibility. Pair with `onDismiss` to close. */
  isOpen: boolean;
  /** Called when the sheet is dismissed via any path (X, backdrop, Escape, post-success). */
  onDismiss: () => void;
  /**
   * Email pre-filled into the field when the sheet opens. Captured once at
   * the moment the sheet opens; later changes to this prop while the sheet
   * is open are intentionally ignored so the user's edits aren't clobbered.
   */
  defaultEmail?: string;
  /**
   * Called once the reset email has been requested successfully, before
   * the sheet auto-dismisses. The parent typically uses this to surface a
   * confirmation toast on the underlying page.
   */
  onSuccess?: (email: string) => void;
}

/**
 * Pro mobile "Forgot your password?" sheet.
 *
 * Replaces the redirect to /forgot-password with an inline bottom sheet:
 * email field + send button. Hits the same `POST /auth/forgot-password`
 * endpoint as the web page. On success the sheet auto-dismisses and the
 * parent shows a confirmation toast via `onSuccess`.
 */
const ProForgotPasswordSheet: React.FC<ProForgotPasswordSheetProps> = ({
  isOpen,
  onDismiss,
  defaultEmail = '',
  onSuccess,
}) => {
  const { t } = useTranslation('pro');
  const { triggerHaptic } = useCapacitorPlatform();
  const inputRef = useRef<HTMLIonInputElement>(null);
  const [email, setEmail] = useState(defaultEmail);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Snapshot defaultEmail on open only — depending on it would clobber
  // mid-edit when the parent's email state updates. Also defer focus
  // past the slide-in mount cycle so the keyboard pops on tap (iOS
  // WebKit keeps the user-gesture context through React's render
  // chain for a brief window, which is enough for setFocus to work).
  useEffect(() => {
    if (!isOpen) return;
    setEmail(defaultEmail);
    setError(null);
    const focusTimer = window.setTimeout(() => {
      inputRef.current?.setFocus();
    }, 120);
    return () => window.clearTimeout(focusTimer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (loading || !email) return;
    setError(null);
    setLoading(true);
    await triggerHaptic(ImpactStyle.Medium);
    try {
      await api.post('/auth/forgot-password', { email });
      onSuccess?.(email);
      onDismiss();
    } catch (err: unknown) {
      const apiError =
        (err as { response?: { data?: { error?: string } } })?.response?.data?.error;
      setError(apiError || t('bottomSheet.forgotPasswordError'));
    } finally {
      setLoading(false);
    }
  };

  const isDisabled = loading || !email;

  return (
    <ProPageModal
      isOpen={isOpen}
      onDismiss={onDismiss}
      title={t('bottomSheet.forgotPasswordTitle')}
    >
      <form onSubmit={handleSubmit} className="px-4 py-4 space-y-4" noValidate>
        <p className="text-sm text-text-secondary dark:text-text-dark-secondary">
          {t('bottomSheet.forgotPasswordIntro')}
        </p>

        <IonInput
          ref={inputRef}
          className="pro-forgot-sheet__input"
          type="email"
          inputmode="email"
          autocomplete="email"
          label={t('bottomSheet.forgotPasswordEmailLabel')}
          labelPlacement="stacked"
          placeholder={t('bottomSheet.forgotPasswordEmailPlaceholder')}
          fill="outline"
          value={email}
          onIonInput={(ev) => setEmail(ev.detail.value ?? '')}
          required
        />

        {error && (
          <div
            role="alert"
            aria-live="assertive"
            className="rounded-md border border-error-200 bg-error-50 px-3 py-2 text-sm text-error-700"
          >
            {error}
          </div>
        )}

        <button
          type="submit"
          disabled={isDisabled}
          className="w-full flex items-center justify-center gap-2 px-4 py-3 rounded-xl bg-primary-700 text-white font-semibold text-sm transition-colors active:bg-primary-800 disabled:opacity-60 disabled:cursor-not-allowed"
        >
          {loading && <IonSpinner name="crescent" className="w-4 h-4" />}
          {loading
            ? t('bottomSheet.forgotPasswordSubmitting')
            : t('bottomSheet.forgotPasswordSubmit')}
        </button>
      </form>
    </ProPageModal>
  );
};

export default ProForgotPasswordSheet;
